import React from 'react';
import {
    Box, Text, Grid, GridItem,
} from '@chakra-ui/react';
import {
    Hide, KeyValuePair, Show, ThemeTokens,
} from '@acp/core-ui';
import { ReportedFinding, ReportedFindingData } from '@acp/shared';
import { format } from 'date-fns';
import { RowLayout } from './reported-findings-row-layout';
import { ReportedFindingTagCollection } from '../wizard/reported-finding-tag-collection';
import { ReportedFindingResolveButton } from '../resolve-reported-finding-button';
import { QuotationTag } from './quotation-tags';
import { ServiceIcons } from '../wizard/service-icons';

const COLORED_BORDER_WIDTH = 6;
const CARD_BORDER_PADDING = 5;
const gap = 4;

function GridWrapper({ children }: { children: React.ReactNode }) {
    return (
        <Grid
            gridTemplateRows="auto"
            gridTemplateAreas={[
                `
            "address address address"
            "service service service"
            "service-date service-date service-date"
            "reported-findings reported-findings reported-findings"
            "status status status"
            "actions actions actions"
        `,
                null,
                null,
                null,
                '"address service service-date reported-findings status actions"',
            ]}
            gridTemplateColumns={[
                '1fr 1fr',
                'min-content 1fr 1fr',
                null,
                '1fr 1fr 1fr 1fr',
                '21% 12% 12% 25% 14% 1fr',
            ]}
            gap={gap}
            alignItems={{ base: 'center', xl: 'start' }}
            justifyItems="start"
            h="auto"
        >
            {children}
        </Grid>
    );
}

function ReportedFindingsRowHeader({
    isOverviewMode = false,
}: {
    isOverviewMode?: boolean;
}) {
    return (
        <Box
            borderBottomWidth={1}
            px={CARD_BORDER_PADDING}
            pb={2}
            fontSize={{ base: 12, lg: 13 }}
            w="full"
            borderLeftWidth={COLORED_BORDER_WIDTH}
            borderLeftColor="transparent"
        >
            <GridWrapper>
                <GridItem area="address">Address</GridItem>
                <GridItem area="service">Service</GridItem>
                {isOverviewMode !== true && (
                    <GridItem area="service-date">Service date</GridItem>
                )}
                <GridItem area="service-date">Service date</GridItem>
                <GridItem area="reported-findings">Reported findings</GridItem>
                <GridItem area="status">Status</GridItem>
                <GridItem area="actions">
                    <Text> </Text>
                </GridItem>
            </GridWrapper>
        </Box>
    );
}

function ReportedFindingTagsRenderer({
    reportedFindings,
    children,
}: {
    reportedFindings: ReportedFindingData[];
    children?: React.ReactNode;
}) {
    return React.Children.count(children) === 0 ? (
        <ReportedFindingTagCollection reportedFindings={reportedFindings} />
    ) : (
        children
    );
}

function ReportedFindingServiceDateColunm({
    serviceDate,
    isOverviewMode = false,
}: {
    serviceDate: string;
    isOverviewMode?: boolean;
}) {
    if (isOverviewMode === true) {
        return null;
    }

    return (
        <RowLayout.Column area="service-date">
            <Show above={ThemeTokens.Defaults.sidebarBreakpoint}>
                <Text>{format(serviceDate, 'dd/MM/yyyy')}</Text>
            </Show>
            <Hide above={ThemeTokens.Defaults.sidebarBreakpoint}>
                <KeyValuePair label="Managed by" align="flex-start" spacing={0}>
                    <Text>{format(serviceDate, 'dd/MM/yyyy')}</Text>
                </KeyValuePair>
            </Hide>
        </RowLayout.Column>
    );
}

function ReportedFindingsRow({
    reportedFinding,
    gridWrapper = GridWrapper,
    children,
    // eslint-disable-next-line max-len
    isOverviewMode = false, // If true, it will show in an overview mode with less columns / information (e.g. dashboard page)
}: {
    reportedFinding: ReportedFinding;
    gridWrapper?: React.ComponentType<{ children: React.ReactNode }>;
    children?: React.ReactNode;
    isOverviewMode?: boolean;
}) {
    const { tags, service } = reportedFinding;
    const { compliancePropertyId, addressLine1, addressLine2 } = reportedFinding.property;
    const { reportedFindings } = reportedFinding;

    const { originalBookingDate } = reportedFindings[0].problemRegister;

    return (
        <RowLayout>
            <RowLayout.Body as={gridWrapper}>
                <RowLayout.Column area="address">
                    <RowLayout.Address
                        propertyId={compliancePropertyId}
                        addressLine1={addressLine1}
                        addressLine2={addressLine2}
                        tags={tags}
                    />
                </RowLayout.Column>

                <GridItem area="service">
                    <Show above={ThemeTokens.Defaults.sidebarBreakpoint}>
                        <ServiceIcons service={service} />
                    </Show>
                    <Hide above={ThemeTokens.Defaults.sidebarBreakpoint}>
                        <KeyValuePair label="Enrolled" align="flex-start" spacing={0}>
                            <ServiceIcons service={service} />
                        </KeyValuePair>
                    </Hide>
                </GridItem>

                <ReportedFindingServiceDateColunm
                    serviceDate={originalBookingDate}
                    isOverviewMode={isOverviewMode}
                />

                <RowLayout.Column area="reported-findings">
                    <RowLayout.ReportedFindings reportedFindings={reportedFindings} />
                </RowLayout.Column>

                <RowLayout.Column area="status">
                    <ReportedFindingTagsRenderer reportedFindings={reportedFindings}>
                        {children}
                    </ReportedFindingTagsRenderer>
                    <QuotationTag
                        tags={service?.tags}
                        property={reportedFinding.property}
                    />
                </RowLayout.Column>

                <RowLayout.Column area="actions">
                    <ReportedFindingResolveButton
                        compliancePropertyId={compliancePropertyId}
                        serviceShortName={service.suffix}
                    />
                </RowLayout.Column>
            </RowLayout.Body>
        </RowLayout>
    );
}

export { ReportedFindingsRow, ReportedFindingsRowHeader };
